import { Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { Link } from "react-router";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { pluralize } from "@/components/helpers/string-utils";
import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { CoachRating } from "@/components/pages_flexible/coach/CoachRating";
import { FlexibleButton } from "@/components/pieces/button/FlexibleButton";
import { AvatarWithPrettyNull } from "@/components/pieces/users/AvatarWithPrettyNull";
import { MIDTEXT_COLOR } from "@/css/constants";
import { PaginatedCoachesQuery } from "@/graphql";

type Coach = PaginatedCoachesQuery["paginatedCoaches"]["data"][number];

interface CoachCardProps {
  coach: Coach;
  onChoose: (isChosen: boolean) => void;
  isChosen: boolean;
  productIds: string[];
}

export function CoachCard({
  coach,
  onChoose,
  isChosen,
  productIds,
}: CoachCardProps) {
  const { coachProducts } = coach || {};
  const selectedProducts = coachProducts?.filter((p) =>
    productIds.includes(p.product.id),
  );

  return (
    <ScenarioCardContainer
      className="bas round-corners"
      flexDirection={["column", "column", "row", "row"]}
    >
      <Flex alignItems="center" flexDirection={["column", "row"]}>
        <Box minWidth={100}>
          <AvatarWithPrettyNull
            user={{
              name: coach?.name,
              photo: coach?.photo,
            }}
            size={100}
          />
        </Box>
        <Box mx={[0, 3]} textAlign="left" mt={[3, 0]}>
          <Flex fontWeight={500} fontSize={18} alignItems={"center"}>
            <Box mr={2}>{coach?.name}</Box>{" "}
            <CoachRating rating={coach?.agentProfile?.averageRating} />
          </Flex>
          {coach?.agentProfile?.summary ? (
            <Box mt={1} color={MIDTEXT_COLOR}>
              {coach.agentProfile.summary}
            </Box>
          ) : null}
          <Flex
            alignItems="center"
            mt={3}
            justifyContent="flex-start"
            className="flex-wrap gap-4"
          >
            {selectedProducts?.map((coachProduct) =>
              coachProduct?.product?.duration ? (
                <Flex
                  key={coachProduct.id}
                  className="rounded-sm bg-gray-200 px-2 py-0.5"
                >
                  <Tooltip
                    content={coachProduct.product.name}
                    popoverClassName="tooltip-break-line"
                  >
                    <div className="flex gap-1.5">
                      <div className="text-sm font-bold text-gray-500">
                        {`${coachProduct.product.duration} Min:`}
                      </div>
                      <div className="flex items-center gap-0.5">
                        <EmbedSvg src={CreditSVG} height={14} width={14} />
                        <Box ml={1} className="text-sm font-normal">
                          {pluralize({
                            amount: coachProduct.coachPrice || 0,
                            singular: "credit",
                            plural: "credits",
                            showCount: true,
                          })}
                        </Box>
                      </div>
                    </div>
                  </Tooltip>
                </Flex>
              ) : null,
            )}
            <Link to={`/@${coach.coachSlug}`} target="_blank">
              <Flex alignItems={"center"}>
                <Box mr={2}>See Profile</Box>
                <Icon icon="share" />
              </Flex>
            </Link>
          </Flex>
        </Box>
      </Flex>
      <Box
        minWidth={160}
        width={["100%", "100%", 160, 160]}
        mt={[24, 24, 0, 0]}
      >
        {!isChosen ? (
          <FlexibleButton
            fill={true}
            onClick={() => null}
            rightIcon={<Icon icon="plus" />}
            onClickCapture={() => onChoose(true)}
          >
            Add Coach
          </FlexibleButton>
        ) : (
          <FlexibleButton
            fill={true}
            rightIcon={<Icon icon="tick" />}
            variant="primary"
            style={{ backgroundColor: "#27AE60" }}
            onClickCapture={() => onChoose(false)}
          >
            Added
          </FlexibleButton>
        )}
      </Box>
    </ScenarioCardContainer>
  );
}

const ScenarioCardContainer = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  color: inherit;
  padding: 16px;
`;
